import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import { db, functions } from '../lib/firebase';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../lib/AuthContext';

export default function HomePage({ onAuditClick }) {
    const { user } = useAuth();
    const [plans, setPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(false);

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const plansRef = collection(db, 'plans');
                const q = query(plansRef, where("active", "==", true), orderBy("tier", "asc"));
                const querySnapshot = await getDocs(q);
                
                const activePlans = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setPlans(activePlans);
            } catch (error) {
                console.error("Failed to fetch public plans:", error);
            } finally {
                setLoadingPlans(false);
            }
        };

        fetchPlans();
    }, []);

    const handleSubscribe = async (priceId) => {
        if (!user) {
            // Unauthenticated users trying to buy -> send to app to login/signup
            window.location.hash = 'app';
            return;
        }

        setCheckoutLoading(true);
        try {
            const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
            const result = await createCheckoutSession({
                priceId: priceId,
                successUrl: `${window.location.origin}/#app`,
                cancelUrl: `${window.location.origin}/#home`
            });
            
            if (result.data?.url) {
                window.location.href = result.data.url; // Redirect to Stripe
            }
        } catch (error) {
            console.error("Failed to start checkout:", error);
            alert("Failed to start checkout process. Please try again.");
            setCheckoutLoading(false);
        }
    };

    return (
        <div className="bg-warm-pearl min-h-screen">
            {/* Hero Section */}
            <section className="px-6 py-24 md:py-32 max-w-7xl mx-auto text-center">
                <h1 className="text-espresso massive-score mb-8">Peace of<br />Mind.</h1>
                <p className="text-espresso text-xl md:text-2xl font-medium max-w-2xl mx-auto mb-12 opacity-90">
                    Is your website welcoming to everyone? We help small businesses make their sites accessible, friendly, and legal-ready.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                    <button
                        onClick={onAuditClick}
                        className="pill-button text-lg px-10 py-4 shadow-lg hover:shadow-xl transition-shadow"
                    >
                        Scan My Website Free
                    </button>
                    <a href="#how-it-works" className="text-espresso font-bold hover:text-teal transition-colors border-b-2 border-espresso/20 pb-1">
                        How it works
                    </a>
                </div>
            </section>

            {/* The Why Section */}
            <section className="bg-stone py-24 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-espresso text-4xl md:text-5xl font-black mb-8">Imagine your customer...</h2>
                    <p className="text-espresso text-lg md:text-xl leading-relaxed opacity-80 mb-12">
                        Imagine a customer trying to read your menu or book a service, but the text is too light or the buttons don't work with their tools.
                        It's frustrating for them and bad for business. <strong>Web accessibility</strong> simply means making sure everyone—regardless of ability—can use your site.
                        We help you fix the gaps so nobody gets left behind.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left mt-16" id="how-it-works">
                        {[
                            { step: "1", title: "Drop your link", desc: "Just enter your website address. Our tool does the heavy lifting." },
                            { step: "2", title: "Get a plain-English grade", desc: "No confusing tech-speak. We'll tell you exactly how you're doing." },
                            { step: "3", title: "Follow simple steps", desc: "We provide easy-to-follow instructions to fix every issue." }
                        ].map((item, i) => (
                            <div key={i} className="bento-card bg-warm-pearl">
                                <div className="text-teal text-4xl font-black mb-4">0{item.step}</div>
                                <h3 className="text-espresso text-xl font-bold mb-2">{item.title}</h3>
                                <p className="text-espresso opacity-70 text-base">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Social Proof */}
            <section className="py-24 px-6 max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-espresso text-3xl font-black uppercase tracking-widest opacity-30">Trusted by Main Street</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    <blockquote className="bento-card">
                        <p className="text-espresso text-lg font-medium italic mb-6">
                            "I was terrified of the legal jargon around accessibility. This tool made it feel like fixing a leaky faucet. Simple and effective."
                        </p>
                        <footer>
                            <cite className="text-teal font-bold not-italic">— Sarah, Owner of The Blooming Florist</cite>
                        </footer>
                    </blockquote>
                    <blockquote className="bento-card">
                        <p className="text-espresso text-lg font-medium italic mb-6">
                            "Running a local bakery is hard enough. Ensuring my menu is readable for everyone shouldn't be. Accessibility Scout is a lifesaver."
                        </p>
                        <footer>
                            <cite className="text-teal font-bold not-italic">— Marco, Head Baker at Crumb & Co.</cite>
                        </footer>
                    </blockquote>
                </div>
            </section>

            {/* Pricing Section */}
            <section className="bg-espresso text-warm-pearl py-24 px-6 relative overflow-hidden" id="pricing">
                {/* Decorative background elements */}
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-10">
                    <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal rounded-full blur-[100px]"></div>
                    <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-teal rounded-full blur-[100px]"></div>
                </div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-black mb-6">Simple, Transparent Pricing</h2>
                        <p className="text-xl opacity-80 max-w-2xl mx-auto">
                            Choose the plan that fits your business needs. Upgrade, downgrade, or cancel at any time.
                        </p>
                    </div>

                    {loadingPlans ? (
                        <div className="flex justify-center p-12">
                            <div className="w-12 h-12 border-4 border-teal/30 border-t-teal rounded-full animate-spin"></div>
                        </div>
                    ) : plans.length === 0 ? (
                        <div className="text-center p-12 opacity-50">
                            <p className="text-xl font-bold">Pricing information is currently unavailable.</p>
                            <p>Please contact support or try again later.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 justify-center items-start">
                            {plans.map((plan, index) => {
                                // Highlight the middle plan or the most expensive if few
                                const isHighlighted = plans.length > 2 && index === Math.floor(plans.length / 2);

                                return (
                                    <div 
                                        key={plan.id} 
                                        className={`rounded-3xl p-8 flex flex-col gap-8 transition-transform hover:-translate-y-2 border ${
                                            isHighlighted 
                                                ? 'bg-teal text-espresso border-teal shadow-2xl shadow-teal/20 scale-105 z-10' 
                                                : 'bg-warm-pearl/5 border-warm-pearl/10 backdrop-blur-sm'
                                        }`}
                                    >
                                        <div>
                                            {isHighlighted && (
                                                <span className="bg-espresso text-warm-pearl text-[0.625rem] font-black uppercase tracking-widest px-3 py-1 rounded-full mb-4 inline-block">
                                                    Most Popular
                                                </span>
                                            )}
                                            <h3 className={`text-2xl font-black ${isHighlighted ? 'text-espresso' : 'text-warm-pearl'}`}>
                                                {plan.name}
                                            </h3>
                                            <div className="mt-4 flex items-end gap-1">
                                                <span className={`text-5xl font-black tracking-tighter ${isHighlighted ? 'text-espresso' : 'text-teal'}`}>
                                                    ${plan.price}
                                                </span>
                                                <span className="opacity-70 font-bold mb-2">/mo</span>
                                            </div>
                                        </div>

                                        <ul className="flex-1 space-y-4">
                                            {[
                                                plan.maxDomains ? `${plan.maxDomains === -1 ? 'Unlimited' : plan.maxDomains} Domain${plan.maxDomains === 1 ? '' : 's'}` : null,
                                                plan.maxPagesPerMonth ? `Up to ${plan.maxPagesPerMonth} pages per month` : null,
                                                plan.historyRetentionDays ? `${plan.historyRetentionDays} Days History` : null,
                                                plan.allowScheduling ? 'Automated Scheduling' : null,
                                                plan.allowLocalAgent ? 'Local Developer Agent' : null,
                                                plan.allowCompanion ? 'Chrome Extension' : null,
                                                plan.allowPdfExport ? 'White-label PDF Reports' : null,
                                                ...(plan.features || [])
                                            ].filter(Boolean).map((feature, i) => (
                                                <li key={i} className="flex gap-3 text-sm font-medium">
                                                    <span className={isHighlighted ? 'text-espresso opacity-60' : 'text-teal'}>✓</span>
                                                    <span className={isHighlighted ? 'text-espresso/90' : 'opacity-90'}>{feature}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        <button 
                                            onClick={() => handleSubscribe(plan.stripePriceId)}
                                            disabled={checkoutLoading || !plan.stripePriceId}
                                            className={`w-full py-4 rounded-xl font-black uppercase tracking-widest transition-all ${
                                                isHighlighted
                                                    ? 'bg-espresso text-warm-pearl hover:bg-stone hover:text-espresso'
                                                    : 'bg-teal text-espresso hover:bg-teal-light cursor-pointer'
                                            } ${(!plan.stripePriceId || checkoutLoading) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            {checkoutLoading ? 'Processing...' : (user ? 'Subscribe Now' : 'Get Started')}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {/* CTA Footer */}
            <section className="bg-espresso text-warm-pearl py-20 px-6 text-center">
                <h2 className="text-4xl font-black mb-8">Ready to be more welcoming?</h2>
                <button
                    onClick={onAuditClick}
                    className="pill-button !bg-warm-pearl !text-espresso hover:!bg-stone"
                >
                    Start Your Free Scan
                </button>
            </section>

            {/* Features & Technology */}
            <section className="py-24 px-6 bg-warm-pearl">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-espresso text-3xl md:text-4xl font-black mb-6">Powerful Tech, Simple Interface</h2>
                        <p className="text-espresso text-lg opacity-80 max-w-2xl mx-auto">
                            Under the hood, Accessibility Scout uses enterprise-grade technology to keep your business safe without the complexity.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="bento-card bg-stone">
                            <div className="text-3xl mb-4">⚡</div>
                            <h3 className="text-espresso text-xl font-bold mb-3">Enterprise Scanning Engine</h3>
                            <p className="text-espresso opacity-70">
                                Powered by Axe-core®, the world's leading accessibility testing engine. It's the same technology used by Fortune 500 companies, simplified for your small business.
                            </p>
                        </div>
                        <div className="bento-card bg-stone">
                            <div className="text-3xl mb-4">🛡️</div>
                            <h3 className="text-espresso text-xl font-bold mb-3">Legal Compliance Ready</h3>
                            <p className="text-espresso opacity-70">
                                Scans against WCAG 2.1 AA standards, which is the baseline requirement for ADA compliance and Section 508. Stay protected from expensive lawsuits.
                            </p>
                        </div>
                        <div className="bento-card bg-stone">
                            <div className="text-3xl mb-4">🔒</div>
                            <h3 className="text-espresso text-xl font-bold mb-3">Private & Secure</h3>
                            <p className="text-espresso opacity-70">
                                We don't store your customer data. Our scans only analyze the public-facing structure of your website, ensuring complete privacy.
                            </p>
                        </div>
                        <div className="bento-card bg-stone">
                            <div className="text-3xl mb-4">🚀</div>
                            <h3 className="text-espresso text-xl font-bold mb-3">Instant Actionable Reports</h3>
                            <p className="text-espresso opacity-70">
                                Get clear, plain-English reports prioritizing exactly what needs to be fixed. Hand it directly to your web developer or use our easy guides to fix it yourself.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
